/**
 * AnyDeskInlineEditor — แสดง/แก้ AnyDesk ID (+ หมายเหตุ) แบบ inline ใช้ร่วมทุกหน้าจอ
 *
 * เจ้าของโปรเจกต์ 23 ก.ย. 2026: "เรื่อง anydesk ค่อนข้างสับสน มีใส่หลายที่ ก็ควรอัพเดทได้หลายที่"
 * → ค่าเดียว (rental_items.anydesk_id ของรอบเช่า) แก้ได้จากหลายหน้า · ทุกหน้าใช้ component นี้
 * ต่างกันแค่ `onSave` ที่ยิงคนละ endpoint (รายการเช่า / อุปกรณ์ / เคสซ่อม)
 *
 * - ปุ่มแก้ไข (ดินสอ) แสดงเฉพาะผู้มีสิทธิ์ `rentals.remote_access` และ `canEdit` ไม่เป็น false
 * - บันทึกส่งเฉพาะ key ที่เปลี่ยน (ไม่ส่ง = ไม่แตะ) · ช่องว่าง = ล้างค่า (null)
 * - ไม่มี "รหัสผ่าน AnyDesk" (PO 2026-09-07)
 */
import React, { useEffect, useState } from 'react';
import { RiComputerLine, RiFileCopyLine, RiPencilLine, RiCheckLine, RiCloseLine } from 'react-icons/ri';
import { cn } from '../../utils/cn';
import { usePermission } from '../../hooks/usePermission';
import { showSuccess, showError } from '../../utils/alert';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import {
  ANYDESK_ID_MAX_LENGTH,
  REMOTE_ACCESS_PERMISSION,
  type RemoteAccessUpdate,
} from '../../services/remoteAccessService';

export interface AnyDeskInlineEditorProps {
  anydeskId?: string | null;
  notes?: string | null;
  /** ยิง endpoint จริง — throw เมื่อไม่สำเร็จ (component แสดง error ให้) */
  onSave: (data: RemoteAccessUpdate) => Promise<unknown>;
  /** false = ซ่อนปุ่มแก้ไขแม้มีสิทธิ์ (เช่น เครื่องไม่อยู่ในสัญญา) */
  canEdit?: boolean;
  /** แสดงช่องหมายเหตุตอนแก้ไข (default true) */
  showNotes?: boolean;
  /** ข้อความเล็กใต้ค่า เช่น "จากสัญญา RNT-…" */
  sourceLabel?: React.ReactNode;
  /** ข้อความเมื่อยังไม่มีค่า (default "ยังไม่ได้กรอก") */
  emptyText?: string;
  /** แสดงหัวข้อ "AnyDesk" ด้านซ้าย (default true) */
  showLabel?: boolean;
  compact?: boolean;
  className?: string;
  testId?: string;
}

const copyToClipboard = (text: string) => {
  try {
    void navigator.clipboard?.writeText(text);
    showSuccess(`คัดลอก ${text} แล้ว`, 'คัดลอกแล้ว');
  } catch {
    /* clipboard ไม่พร้อม (http/permission) — เงียบ */
  }
};

const AnyDeskInlineEditor: React.FC<AnyDeskInlineEditorProps> = ({
  anydeskId,
  notes,
  onSave,
  canEdit = true,
  showNotes = true,
  sourceLabel,
  emptyText = 'ยังไม่ได้กรอก',
  showLabel = true,
  compact = false,
  className,
  testId = 'anydesk-inline',
}) => {
  const { hasPermission } = usePermission();
  const allowed = canEdit && hasPermission(REMOTE_ACCESS_PERMISSION);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [idDraft, setIdDraft] = useState(anydeskId ?? '');
  const [notesDraft, setNotesDraft] = useState(notes ?? '');

  useEffect(() => {
    if (!editing) {
      setIdDraft(anydeskId ?? '');
      setNotesDraft(notes ?? '');
    }
  }, [anydeskId, notes, editing]);

  const startEdit = () => {
    setIdDraft(anydeskId ?? '');
    setNotesDraft(notes ?? '');
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setIdDraft(anydeskId ?? '');
    setNotesDraft(notes ?? '');
  };

  const save = async () => {
    const payload: RemoteAccessUpdate = {};
    const newId = idDraft.trim();
    if (newId !== (anydeskId ?? '').trim()) payload.anydesk_id = newId || null;
    if (showNotes) {
      const newNotes = notesDraft.trim();
      if (newNotes !== (notes ?? '').trim()) payload.remote_access_notes = newNotes || null;
    }
    if (Object.keys(payload).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(payload);
      showSuccess('บันทึก AnyDesk แล้ว');
      setEditing(false);
    } catch (error) {
      showError(getApiErrorMessage(error, 'บันทึก AnyDesk ไม่สำเร็จ'));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className={cn('space-y-2', className)} data-testid={testId}>
        <div className="flex flex-wrap items-center gap-2">
          {showLabel && (
            <span className="flex items-center gap-1 text-sm text-secondary-500">
              <RiComputerLine className="h-4 w-4" />
              AnyDesk
            </span>
          )}
          <input
            type="text"
            className="input font-mono text-sm py-1 max-w-[16rem]"
            aria-label="AnyDesk ID"
            placeholder="เช่น 123 456 789"
            maxLength={ANYDESK_ID_MAX_LENGTH}
            value={idDraft}
            onChange={(e) => setIdDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); void save(); }
              if (e.key === 'Escape') { e.preventDefault(); cancel(); }
            }}
            autoFocus
            disabled={saving}
          />
        </div>
        {showNotes && (
          <input
            type="text"
            className="input text-sm py-1 w-full"
            aria-label="หมายเหตุการเข้าถึงระยะไกล"
            placeholder="หมายเหตุ (ไม่บังคับ)"
            maxLength={500}
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            disabled={saving}
          />
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn btn-primary px-3 py-1 text-sm inline-flex items-center gap-1"
            onClick={() => void save()}
            disabled={saving}
          >
            <RiCheckLine className="h-4 w-4" />
            {saving ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
          <button
            type="button"
            className="btn btn-secondary px-3 py-1 text-sm inline-flex items-center gap-1"
            onClick={cancel}
            disabled={saving}
          >
            <RiCloseLine className="h-4 w-4" />
            ยกเลิก
          </button>
          <span className="text-xs text-secondary-400">เว้นว่าง = ล้างค่า</span>
        </div>
      </div>
    );
  }

  const value = (anydeskId ?? '').trim();
  return (
    <div className={cn(compact ? 'text-xs' : 'text-sm', className)} data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2">
        {showLabel && (
          <span className="flex items-center gap-1 text-secondary-500">
            <RiComputerLine className="h-4 w-4" />
            AnyDesk
          </span>
        )}
        {value ? (
          <>
            <span className="font-mono font-medium text-secondary-900" data-testid={`${testId}-value`}>{value}</span>
            <button
              type="button"
              className="inline-flex min-h-[24px] min-w-[24px] items-center justify-center rounded text-secondary-400 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
              title="คัดลอก AnyDesk ID"
              aria-label="คัดลอก AnyDesk ID"
              onClick={() => copyToClipboard(value)}
            >
              {/* BUG-S2190-001: พื้นที่กด ≥24px (WCAG 2.5.8) — ไอคอน 16px เดิมกดยากบนมือถือ */}
              <RiFileCopyLine className="h-4 w-4" />
            </button>
          </>
        ) : (
          <span className="italic text-secondary-400" data-testid={`${testId}-empty`}>{emptyText}</span>
        )}
        {allowed && (
          <button
            type="button"
            className="inline-flex min-h-[24px] min-w-[24px] items-center justify-center rounded text-secondary-400 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
            title="แก้ไข AnyDesk"
            aria-label="แก้ไข AnyDesk"
            onClick={startEdit}
          >
            <RiPencilLine className="h-4 w-4" />
          </button>
        )}
      </div>
      {notes && <p className="mt-0.5 text-secondary-600">{notes}</p>}
      {sourceLabel && <p className="mt-0.5 text-xs text-secondary-500">{sourceLabel}</p>}
    </div>
  );
};

export default AnyDeskInlineEditor;
